import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { useGet, usePut } from "../../api";
import {
  IKindredCouponDetail,
  IKindredCouponUpdate,
  IKindredEntityUpdateRequest,
  IKindredMetadataUpdate,
  KindredPublishStatus,
  KindredCouponCoverage,
  CouponOverridesFlag,
} from "../../interfaces";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Copy, Check, Edit2, X, Save } from "react-feather";
import { format } from "date-fns";
import { MetadataCard } from "./offer-detail";
import {
  BrandTypeahead,
  TagListEditor,
  OverrideFlagsEditor,
  OverrideFlagWarning,
  missingOverrides,
  withOverrides,
  COUPON_OVERRIDE_REQUIREMENTS,
} from "../../components/workbench";

const statusLabel = (status: number) => {
  const map: Record<number, { bg: string; label: string }> = {
    [KindredPublishStatus.Draft]: { bg: "secondary", label: "Draft" },
    [KindredPublishStatus.AwaitingApproval]: {
      bg: "warning",
      label: "Awaiting Approval",
    },
    [KindredPublishStatus.Approved]: { bg: "success", label: "Approved" },
    [KindredPublishStatus.Deactivated]: {
      bg: "danger",
      label: "Deactivated",
    },
  };
  const info = map[status] || { bg: "secondary", label: String(status) };
  return <Badge bg={info.bg}>{info.label}</Badge>;
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "yyyy-MM-dd HH:mm");
  } catch {
    return dateStr;
  }
};

const CopyIdButton: React.FC<{ id: string }> = ({ id }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="link"
      size="sm"
      className="p-0 ms-1 align-baseline"
      title={`Copy ${id}`}
      onClick={() => {
        navigator.clipboard.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? (
        <Check size={12} className="text-success" />
      ) : (
        <Copy size={12} />
      )}
    </Button>
  );
};

export const KindredCouponDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch } = useGet<IKindredCouponDetail>(
    `kindred/coupons/${id}`,
    true
  );

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<IKindredCouponUpdate>({});
  const [metadataForm, setMetadataForm] = useState<IKindredMetadataUpdate>({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [flagsAcknowledged, setFlagsAcknowledged] = useState(false);
  const [comment, setComment] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const updateCoupon = usePut<
    IKindredCouponDetail,
    IKindredEntityUpdateRequest<IKindredCouponUpdate>
  >();

  useEffect(() => {
    if (data && editing) {
      setForm({
        code: data.code,
        brandId: data.brandId,
        publishedStatus: data.publishedStatus,
        discount: data.discount,
        summary: data.summary,
        validFrom: data.validFrom,
        validTo: data.validTo,
        priority: data.priority,
        coverage: data.coverage,
        lastTestedAt: data.lastTestedAt,
        overridesFlag: data.overridesFlag ?? 0,
        domains: data.couponDomainDetails || [],
        countries: data.countryDetails?.map((c) => c.countryCode) || [],
      });
      const m = data.metadata;
      setMetadataForm({
        promoType: m?.promoType ?? null,
        coverage: m?.coverage ?? null,
        discountType: m?.discountType ?? null,
        discounts: m?.discounts ?? null,
        nonMonetaryValue: m?.nonMonetaryValue ?? null,
        language: m?.language ?? null,
        currency: m?.currency ?? null,
        scope: m?.scope ?? null,
        channel: m?.channel ?? null,
        productCategory: m?.productCategory ?? null,
        rawCategory: m?.rawCategory ?? null,
        isStackable: m?.isStackable ?? null,
        requiresReview: m?.requiresReview ?? null,
        reasoning: m?.reasoning ?? null,
        isReviewed: m?.isReviewed ?? null,
      });
    }
  }, [data, editing]);

  const startEdit = () => {
    setSaveError(null);
    setSaveSuccess(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm({});
    setMetadataForm({});
    setComment("");
    setSaveError(null);
    setFlagsAcknowledged(false);
  };

  const buildChanges = (): Partial<IKindredCouponUpdate> => {
    const changed: Partial<IKindredCouponUpdate> = {};
    for (const key of Object.keys(form) as (keyof IKindredCouponUpdate)[]) {
      if (key === "metadata" || key === "domains" || key === "countries")
        continue;
      if (
        data &&
        String(form[key] ?? "") !== String((data as any)[key] ?? "")
      ) {
        (changed as any)[key] = form[key];
      }
    }

    // Detect array field changes (domains, countries)
    const origDomains = data?.couponDomainDetails || [];
    if (JSON.stringify(form.domains) !== JSON.stringify(origDomains)) {
      changed.domains = form.domains;
    }
    const origCountries = data?.countryDetails?.map((c) => c.countryCode) || [];
    if (JSON.stringify(form.countries) !== JSON.stringify(origCountries)) {
      changed.countries = form.countries;
    }

    // Detect metadata changes
    const metaChanged: IKindredMetadataUpdate = {};
    const origMeta = data?.metadata;
    for (const key of Object.keys(
      metadataForm
    ) as (keyof IKindredMetadataUpdate)[]) {
      const newVal = metadataForm[key];
      const oldVal = origMeta ? (origMeta as any)[key] : undefined;
      if (String(newVal ?? "") !== String(oldVal ?? "")) {
        (metaChanged as any)[key] = newVal;
      }
    }
    if (Object.keys(metaChanged).length > 0) {
      changed.metadata = metaChanged;
    }
    return changed;
  };

  const pendingFlag = Number(form.overridesFlag ?? data?.overridesFlag ?? 0);
  const missingFlags = missingOverrides(
    buildChanges(),
    pendingFlag,
    COUPON_OVERRIDE_REQUIREMENTS
  );

  const handleSave = async () => {
    try {
      setSaveError(null);
      const changed = buildChanges();

      if (Object.keys(changed).length === 0) {
        setSaveError("No changes detected");
        return;
      }
      if (changed.publishedStatus !== undefined) {
        changed.publishedStatus = Number(changed.publishedStatus);
        // API requires a reason when deactivating; the save comment is it.
        if (changed.publishedStatus === KindredPublishStatus.Deactivated) {
          changed.reason = comment.trim();
        }
      }
      if (changed.discount !== undefined) {
        changed.discount = Number(changed.discount);
      }
      if (changed.coverage !== undefined) {
        changed.coverage = Number(changed.coverage);
      }
      if (changed.priority !== undefined) {
        changed.priority = Number(changed.priority);
      }
      if (changed.overridesFlag !== undefined) {
        changed.overridesFlag = Number(changed.overridesFlag);
      }
      await updateCoupon.execute(`kindred/coupons/${id}`, {
        data: changed,
        comment,
      });
      setShowSaveModal(false);
      setEditing(false);
      setComment("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      await refetch();
    } catch (e: any) {
      setSaveError(e?.message || "Failed to save changes");
    }
  };

  const updateField = (field: keyof IKindredCouponUpdate, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateMetadataField = (
    field: keyof IKindredMetadataUpdate,
    value: any
  ) => {
    setMetadataForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" /> Loading coupon...
      </div>
    );
  }

  if (error) {
    return <div className="text-danger">Error: {error.message}</div>;
  }

  if (!data) {
    return <div className="text-muted">Coupon not found.</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>
          Coupon: <code>{data.code}</code>
        </h4>
        <div className="d-flex gap-2">
          <Link to="/kindred/coupons">
            <Button size="sm" variant="outline-secondary">
              ← Back to Coupons
            </Button>
          </Link>
          <Link to={`/kindred/coupons/${id}/audit`}>
            <Button size="sm" variant="outline-info">
              View Audit Trail
            </Button>
          </Link>
          {!editing ? (
            <Button size="sm" variant="primary" onClick={startEdit}>
              <Edit2 size={14} className="me-1" /> Edit
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={cancelEdit}
              >
                <X size={14} className="me-1" /> Cancel
              </Button>
              <Button
                size="sm"
                variant="success"
                onClick={() => setShowSaveModal(true)}
              >
                <Save size={14} className="me-1" /> Save
              </Button>
            </>
          )}
        </div>
      </div>

      {saveSuccess && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSaveSuccess(false)}
        >
          Coupon updated successfully.
        </Alert>
      )}
      {saveError && !showSaveModal && (
        <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Row className="mb-3">
        <Col md={6}>
          <Card>
            <Card.Header>Coupon Details</Card.Header>
            <Card.Body>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      ID
                    </td>
                    <td>{data.id}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Code</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          value={form.code ?? ""}
                          onChange={(e) => updateField("code", e.target.value)}
                        />
                      ) : (
                        <code>{data.code}</code>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Brand</td>
                    <td>
                      {editing ? (
                        <BrandTypeahead
                          brandId={form.brandId || data.brandId}
                          brandName={data.brandName}
                          onChange={(brandId) =>
                            updateField("brandId", brandId)
                          }
                        />
                      ) : (
                        <>
                          <Link to={`/kindred/brands/${data.brandId}`}>
                            {data.brandName}
                          </Link>
                          {data.isTopBrand && (
                            <Badge bg="info" className="ms-1">
                              ★
                            </Badge>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Status</td>
                    <td>
                      {editing ? (
                        <Form.Select
                          size="sm"
                          value={form.publishedStatus ?? 0}
                          onChange={(e) =>
                            updateField(
                              "publishedStatus",
                              Number(e.target.value)
                            )
                          }
                        >
                          <option value={0}>Draft</option>
                          <option value={1}>Awaiting Approval</option>
                          <option value={2}>Approved</option>
                          <option value={3}>Deactivated</option>
                        </Form.Select>
                      ) : (
                        statusLabel(data.publishedStatus)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Source</td>
                    <td>{data.source}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Discount</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="number"
                          value={form.discount ?? 0}
                          onChange={(e) =>
                            updateField("discount", Number(e.target.value))
                          }
                        />
                      ) : (
                        <>{data.discount}%</>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Priority</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="number"
                          value={form.priority ?? 0}
                          onChange={(e) =>
                            updateField("priority", Number(e.target.value))
                          }
                        />
                      ) : (
                        data.priority
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Coverage</td>
                    <td>
                      {editing ? (
                        <Form.Select
                          size="sm"
                          value={form.coverage ?? 0}
                          onChange={(e) =>
                            updateField("coverage", Number(e.target.value))
                          }
                        >
                          <option value={0}>Not Set</option>
                          <option value={1}>Site-Wide</option>
                          <option value={2}>Category</option>
                          <option value={4}>Product Specific</option>
                        </Form.Select>
                      ) : data.coverage === KindredCouponCoverage.None ? (
                        "Not Set"
                      ) : data.coverage === KindredCouponCoverage.SiteWide ? (
                        "Site-Wide"
                      ) : data.coverage === KindredCouponCoverage.Category ? (
                        "Category"
                      ) : data.coverage === KindredCouponCoverage.Product ? (
                        "Product Specific"
                      ) : (
                        "Not Set"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Is SiteWide</td>
                    <td>
                      {data.coverage === KindredCouponCoverage.SiteWide ? (
                        <Badge bg="success">Yes</Badge>
                      ) : (
                        <Badge bg="secondary">No</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Coupon Hash</td>
                    <td>
                      <code className="small">{data.couponHash}</code>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>Dates & Flags</Card.Header>
            <Card.Body>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      Valid From
                    </td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="datetime-local"
                          value={
                            form.validFrom
                              ? form.validFrom.substring(0, 16)
                              : ""
                          }
                          onChange={(e) =>
                            updateField(
                              "validFrom",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : ""
                            )
                          }
                        />
                      ) : (
                        formatDate(data.validFrom)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Valid To</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="datetime-local"
                          value={
                            form.validTo ? form.validTo.substring(0, 16) : ""
                          }
                          onChange={(e) =>
                            updateField(
                              "validTo",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : ""
                            )
                          }
                        />
                      ) : (
                        formatDate(data.validTo)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Expiry Date</td>
                    <td>{formatDate(data.expiryDate)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Deactivated At</td>
                    <td>{formatDate(data.deactivatedAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Created</td>
                    <td>{formatDate(data.createdAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Updated</td>
                    <td>{formatDate(data.updatedAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Last Tested</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="datetime-local"
                          value={
                            form.lastTestedAt
                              ? form.lastTestedAt.substring(0, 16)
                              : data.lastTestedAt
                                ? data.lastTestedAt.substring(0, 16)
                                : ""
                          }
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              lastTestedAt: e.target.value
                                ? new Date(e.target.value).toISOString()
                                : undefined,
                            }))
                          }
                        />
                      ) : (
                        formatDate(data.lastTestedAt)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Overrides Flag</td>
                    <td>
                      {editing ? (
                        <OverrideFlagsEditor
                          value={form.overridesFlag ?? 0}
                          onChange={(overridesFlag) =>
                            setForm((prev) => ({ ...prev, overridesFlag }))
                          }
                          flagEnum={CouponOverridesFlag}
                        />
                      ) : (
                        <OverrideFlagsEditor
                          value={data.overridesFlag}
                          flagEnum={CouponOverridesFlag}
                          readOnly
                        />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Prohibited Contents</td>
                    <td>{data.hasProhibitedContents ? "Yes" : "No"}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Review Flags</td>
                    <td>{data.couponCodeReviewFlags}</td>
                  </tr>
                  {data.reason && (
                    <tr>
                      <td className="fw-bold">Reason</td>
                      <td>{data.reason}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {(data.summary || editing) && (
        <Card className="mb-3">
          <Card.Header>Summary</Card.Header>
          <Card.Body>
            {editing ? (
              <Form.Control
                as="textarea"
                rows={3}
                value={form.summary ?? ""}
                onChange={(e) => updateField("summary", e.target.value)}
              />
            ) : (
              <p>{data.summary}</p>
            )}
          </Card.Body>
        </Card>
      )}

      <MetadataCard
        metadata={data.metadata}
        hashLabel="Coupon Hash"
        hashValue={data.couponHash}
        editing={editing}
        metadataForm={metadataForm}
        onMetadataChange={updateMetadataField}
      />

      <Row>
        <Col md={4}>
          {editing ? (
            <TagListEditor
              label="Countries"
              items={form.countries || []}
              onChange={(countries) =>
                setForm((prev) => ({ ...prev, countries }))
              }
              placeholder="Add country code..."
            />
          ) : (
            <Card className="mb-3">
              <Card.Header>
                Countries ({data.countryDetails?.length ?? 0})
              </Card.Header>
              <Card.Body>
                {data.countryDetails.length === 0 ? (
                  <span className="text-muted">No countries</span>
                ) : (
                  data.countryDetails.map((c) => (
                    <Badge
                      key={c.countryCode}
                      bg={c.isEnabled ? "success" : "secondary"}
                      className="me-1 mb-1"
                    >
                      {c.countryCode}
                      {!c.isEnabled && " (disabled)"}
                    </Badge>
                  ))
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
        <Col md={4}>
          {editing ? (
            <TagListEditor
              label="Coupon Domains"
              normalize="lower"
              items={form.domains || []}
              onChange={(domains) => setForm((prev) => ({ ...prev, domains }))}
              placeholder="Add domain..."
            />
          ) : (
            <Card className="mb-3">
              <Card.Header>
                Coupon Domains ({data.couponDomainDetails?.length ?? 0})
              </Card.Header>
              <Card.Body>
                {data.couponDomainDetails.length === 0 ? (
                  <span className="text-muted">No coupon domains</span>
                ) : (
                  data.couponDomainDetails.map((d) => (
                    <Badge key={d} bg="light" text="dark" className="me-1 mb-1">
                      {d}
                    </Badge>
                  ))
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
        <Col md={8}>
          <CampaignDomainsCard details={data.campaignDomainDetails} />
        </Col>
      </Row>

      {/* Save Comment Modal */}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Comment (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe the reason for this change..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Form.Group>
          <OverrideFlagWarning
            missing={missingFlags}
            acknowledged={flagsAcknowledged}
            onAcknowledge={setFlagsAcknowledged}
            onApply={() =>
              updateField(
                "overridesFlag",
                withOverrides(pendingFlag, missingFlags)
              )
            }
          />
          {saveError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {saveError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            disabled={
              !comment.trim() ||
              updateCoupon.loading ||
              (missingFlags.length > 0 && !flagsAcknowledged)
            }
            onClick={handleSave}
          >
            {updateCoupon.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

/* ── Campaign Domains card with grouped / detailed toggle ────────── */

const CampaignDomainsCard: React.FC<{
  details: IKindredCouponDetail["campaignDomainDetails"];
}> = ({ details }) => {
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [validatedOnly, setValidatedOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedSource, setSelectedSource] = useState("");

  // Unique brands for dropdown
  const brands = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const d of details) {
      if (d.brandId && d.brandName) map.set(d.brandId, d.brandName);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [details]);

  // Unique sources/networks for dropdown
  const sources = React.useMemo(() => {
    const set = new Set<string>();
    for (const d of details) {
      if (d.campaignSource) set.add(d.campaignSource);
    }
    return Array.from(set).sort();
  }, [details]);

  // Filter then sort by domain
  const filtered = React.useMemo(() => {
    let base = validatedOnly ? details.filter((d) => d.validated) : details;
    if (selectedBrand) {
      base = base.filter((d) => d.brandId === selectedBrand);
    }
    if (selectedSource) {
      base = base.filter((d) => d.campaignSource === selectedSource);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      base = base.filter((d) => d.domain.toLowerCase().includes(q));
    }
    return [...base].sort((a, b) => a.domain.localeCompare(b.domain));
  }, [details, validatedOnly, search, selectedBrand, selectedSource]);

  // Grouped view: unique (domain, brandId, validated)
  const grouped = React.useMemo(() => {
    const map = new Map<
      string,
      {
        domain: string;
        brandId: string | null;
        brandName: string | null;
        validated: boolean;
        campaignCount: number;
      }
    >();
    for (const d of filtered) {
      const key = `${d.domain}||${d.brandId}||${d.validated}`;
      const existing = map.get(key);
      if (existing) {
        existing.campaignCount++;
      } else {
        map.set(key, {
          domain: d.domain,
          brandId: d.brandId,
          brandName: d.brandName,
          validated: d.validated,
          campaignCount: 1,
        });
      }
    }
    return Array.from(map.values());
  }, [filtered]);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>
          Campaign Domains ({filtered.length}
          {validatedOnly || search.trim() || selectedBrand || selectedSource
            ? ` of ${details.length}`
            : ""}
          )
        </span>
        <div className="d-flex gap-2 align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ width: 180 }}
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
          >
            <option value="">All Brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: 160 }}
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
          >
            <option value="">All Networks</option>
            {sources.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search domain…"
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant={validatedOnly ? "success" : "outline-secondary"}
            size="sm"
            onClick={() => setValidatedOnly(!validatedOnly)}
          >
            {validatedOnly ? "Validated Only" : "All"}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowCampaigns(!showCampaigns)}
          >
            {showCampaigns ? "Group by Domain" : "Show Campaigns"}
          </Button>
        </div>
      </Card.Header>
      <Card.Body style={{ maxHeight: 400, overflowY: "auto", padding: 0 }}>
        {filtered.length === 0 ? (
          <span className="text-muted p-3 d-block">No campaign domains</span>
        ) : showCampaigns ? (
          <Table size="sm" className="mb-0">
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
            >
              <tr>
                <th className="ps-3">Domain</th>
                <th>Campaign</th>
                <th>Network</th>
                <th>Brand</th>
                <th>Validated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => (
                <tr key={i}>
                  <td className="ps-3">{d.domain}</td>
                  <td>
                    <Link
                      to={`/kindred/deals/${d.campaignId}`}
                      title={d.campaignName ?? d.campaignId}
                    >
                      {d.campaignName
                        ? `${d.campaignName.substring(0, 30)}${d.campaignName.length > 30 ? "…" : ""}`
                        : d.campaignId}
                    </Link>
                  </td>
                  <td>{d.campaignSource ?? "—"}</td>
                  <td>
                    {d.brandName ?? "—"}
                    {d.brandId && <CopyIdButton id={d.brandId} />}
                  </td>
                  <td>
                    {d.validated ? (
                      <Badge bg="success">Yes</Badge>
                    ) : (
                      <Badge bg="secondary">No</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Table size="sm" className="mb-0">
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
            >
              <tr>
                <th className="ps-3">Domain</th>
                <th>Brand</th>
                <th>Validated</th>
                <th>Campaigns</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g, i) => (
                <tr key={i}>
                  <td className="ps-3">{g.domain}</td>
                  <td>
                    {g.brandName ?? "—"}
                    {g.brandId && <CopyIdButton id={g.brandId} />}
                  </td>
                  <td>
                    {g.validated ? (
                      <Badge bg="success">Yes</Badge>
                    ) : (
                      <Badge bg="secondary">No</Badge>
                    )}
                  </td>
                  <td>
                    <Badge bg="light" text="dark">
                      {g.campaignCount}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};
